/**
 * CABVersionHistory — Version history dropdown for CAB.
 *
 * - Versions are numbered in time order: Version 1 is the oldest and the
 *   current CAB has the highest number. The current CAB is listed first, then
 *   the earlier versions, newest first.
 * - Each row shows when that version became the current CAB. The current CAB
 *   reads "Restored ..." when a restore brought it back.
 * - Selecting a version previews it. Restore works like going back: that
 *   version becomes the current CAB again under its own number and the
 *   versions after it leave the history (the page's restore dialog says when
 *   their files are deleted).
 */

import React, { useState } from 'react';
import { Badge, Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Button } from '../Button/Button';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import type { CABCurrentVersion, CABVersion } from '../../services/cabApi';

interface CABVersionHistoryProps {
  versions: CABVersion[];
  /** The current CAB's place in the history; null when there is no current CAB. */
  current: CABCurrentVersion | null;
  selectedTimestampMs: number | null;
  onSelectVersion: (timestampMs: number) => void;
  onClearSelection: () => void;
  onRestoreVersion: (timestampMs: number) => Promise<void> | void;
  /** Called when the panel opens, so the page can reload the list (other people's saves don't update it live). */
  onOpen?: () => void;
  isRestoring?: boolean;
  /** Set while restoring is not allowed (a sync review waits for a decision): disables Restore and says why. */
  restoreDisabledReason?: string;
  permissions?: string[];
}

/**
 * When a version became the current CAB: "Oct 7, 6:11 PM" (the year only when
 * it is not this year), with the full time to the second in a tooltip. Also
 * used by the page's restore dialog. en-US so the format is the same in every
 * browser, as in utils/dateUtils.
 */
export const CABVersionTime: React.FC<{ timestampMs: number }> = ({ timestampMs }) => {
  const date = new Date(timestampMs);
  const isThisYear = date.getFullYear() === new Date().getFullYear();
  return (
    <span
      title={date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      })}
    >
      {date.toLocaleString('en-US', {
        year: isThisYear ? undefined : 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })}
    </span>
  );
};

// The row being previewed (the current CAB when none is) is highlighted.
const rowClass = (highlighted: boolean) =>
  `flex items-center gap-bg-v2-md pl-bg-v2-lg pr-bg-v2-xl py-bg-v2-md border-b border-border-secondary last:border-b-0 border-l-4 transition-colors ${highlighted
    ? 'bg-utility-brand-100 border-l-utility-brand-500'
    : 'hover:bg-bg-primary-hover border-l-transparent'
  }`;

export const CABVersionHistory: React.FC<CABVersionHistoryProps> = ({
  versions,
  current,
  selectedTimestampMs,
  onSelectVersion,
  onClearSelection,
  onRestoreVersion,
  onOpen,
  isRestoring = false,
  restoreDisabledReason,
  permissions,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // A current CAB has a history to show even before it has earlier versions.
  if (versions.length === 0 && !current) return null;

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div className="relative">
        <Button
          type="button"
          onClick={() => {
            if (!isOpen) onOpen?.();
            setIsOpen(!isOpen);
          }}
          variant="outline"
          size="very-small"
          className={`!w-8 !h-8 !p-0 !min-w-0 ${(selectedTimestampMs != null || isOpen) ? '!bg-bg-primary-hover' : ''}`}
          title="Version history"
          ariaLabel="Version history"
          tooltipPosition="bottom"
          label={<Icon name={IconName.History} size="sm" inheritColor />}
        />

        {isOpen && (
          <div className="absolute right-0 mt-bg-v2-md w-96 bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-xl border border-border-secondary z-[60] max-h-[32rem] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-bg-v2-xl py-bg-v2-md border-b border-border-secondary bg-bg-primary-alt">
              <div className="flex items-center justify-between gap-bg-v2-md">
                <div>
                  <Text variant="text-md" weight="semibold" color="primary">
                    Version history
                  </Text>
                  {current && (
                    <Text variant="text-xs" color="secondary" className="mt-bg-v2-xxs">
                      Version {current.number} is current
                    </Text>
                  )}
                </div>
                {selectedTimestampMs != null && (
                  <Button
                    type="button"
                    onClick={onClearSelection}
                    variant="outline"
                    size="small"
                    label="View Current"
                  />
                )}
              </div>
            </div>

            {/* Version list: the current CAB, then the earlier versions, newest first */}
            <div className="flex-1 overflow-y-auto">
              {current && (
                <div className={rowClass(selectedTimestampMs == null)}>
                  <button type="button" onClick={onClearSelection} className="flex-1 min-w-0 text-left">
                    <span className="flex flex-wrap items-center gap-bg-v2-sm">
                      <Text as="span" variant="text-sm" weight="semibold" color="primary">
                        Version {current.number}
                      </Text>
                      <Badge tone="brand">Current</Badge>
                    </span>
                    <Text as="span" variant="text-xs" color="secondary" className="block mt-bg-v2-xxs">
                      {current.restored && 'Restored '}
                      <CABVersionTime timestampMs={current.became_current_ms} />
                    </Text>
                  </button>
                </div>
              )}

              {versions.map((version) => {
                const isSelected = selectedTimestampMs === version.timestamp_ms;

                return (
                  <div key={version.filename} className={rowClass(isSelected)}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectVersion(version.timestamp_ms);
                        setIsOpen(false);
                      }}
                      className="flex-1 min-w-0 text-left"
                    >
                      <span className="flex flex-wrap items-center gap-bg-v2-sm">
                        <Text as="span" variant="text-sm" weight="semibold" color="primary">
                          Version {version.number}
                        </Text>
                        {isSelected && <Badge tone="brand">Viewing</Badge>}
                      </span>
                      <Text as="span" variant="text-xs" color="secondary" className="block mt-bg-v2-xxs">
                        <CABVersionTime timestampMs={version.became_current_ms} />
                      </Text>
                    </button>

                    <IfHasPermission
                      permissions={permissions}
                      requires={Permissions.UpdateProjectPermission}
                    >
                      <Button
                        type="button"
                        onClick={() => {
                          setIsOpen(false);
                          onRestoreVersion(version.timestamp_ms);
                        }}
                        disabled={isRestoring || !!restoreDisabledReason}
                        title={restoreDisabledReason}
                        tooltipPosition="left"
                        variant="outline"
                        size="small"
                        className="shrink-0"
                        label="Restore"
                        ariaLabel={`Restore Version ${version.number}`}
                      />
                    </IfHasPermission>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-bg-v2-xl py-bg-v2-md bg-bg-primary-alt border-t border-border-secondary">
              <Text variant="text-xs" color="secondary">
                Click a version to preview it.
                {/* Only people who can restore are told what a restore does. */}
                <IfHasPermission permissions={permissions} requires={Permissions.UpdateProjectPermission}>
                  {' '}Restoring a version removes the versions after it.
                </IfHasPermission>
              </Text>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
